/* prettier-ignore */

import { Injectable, NgZone, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DataChatService } from './dataChat.service';
import { ChatBotTextAnimationService } from './textAnimationService.service';
import { ChatActionHandlerService } from './chatActionsHandler.service';
import { ChatPerfService } from './chatPerf.service';
import { DebugService } from 'src/app/services/debug.service';
import { RestService } from 'src/app/services/rest.service';
import { Subject, Subscription, concatMap, catchError, timeout, delay, of, from, EMPTY } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { EditRouteService } from 'src/app/pages/custom-route/shared/planner/components/route-timeline/components/daily-route-list/services';
import { CustomRouteService } from 'src/app/pages/custom-route/services';
/**
 * HRS-2418 — Camino de streaming por SSE (alternativa al WebSocket).
 *
 * Flujo (camino stream v3 del asistente, verificado E2E en local 29-jun):
 *   1. POST {apiAssistantUrlBase}/api/v3/ask  { user_id, service, content } -> { status:"queued", message_id }
 *   2. EventSource GET {apiAssistantUrlBase}/api/v3/stream/{message_id}  (eventos tipados en vivo)
 *   3. GET {apiAssistantUrlBase}/api/chat/response/{message_id}  -> { status, content }  (red de seguridad)
 *
 * El endpoint /api/v3/stream emite eventos JSON tipados:
 *   {type:"token",content}            fragmento en vivo del texto
 *   {type:"action_tag",content}       tag inline [ACTION:SHOW_POI(S)/WEATHER/WEB:{...}]
 *   {type:"action",action}            acción estructurada (p.ej. update_interests)
 *   {type:"final",text}               texto definitivo post-procesado (reconciliación)
 *   {type:"done"}                     fin del stream
 *
 * Estrategia:
 *   - Se abre el EventSource SIEMPRE. Llegan frames token/action/action_tag en vivo
 *     (reusando el MISMO render que el WS) y el stream toma el control (cancela el polling).
 *   - El polling de GET /api/chat/response queda como red de seguridad por si el stream
 *     no emitiera tokens (se omite en cuanto llega el primer token).
 *
 */
@Injectable({ providedIn: 'root' })
export class AskStreamingService {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dataChat = inject(DataChatService);
  private readonly textAnimation = inject(ChatBotTextAnimationService);
  private readonly actionService = inject(ChatActionHandlerService);
  private readonly perf = inject(ChatPerfService);
  private readonly debug = inject(DebugService);
  private readonly ngZone = inject(NgZone);
  // Estado del planner para el payload de /api/v3/ask (mismas señales que el WS).
  private readonly customRouteService = inject(CustomRouteService);
  private readonly editRouteService = inject(EditRouteService);
  private readonly rest = inject(RestService);

  private eventSource?: EventSource;
  private tokenReceived = false;
  /** llegó el 'final' del stream: el texto definitivo ya está pintado */
  private finalReceived = false;
  /** el stream se cortó tras pintar tokens: el polling vuelve a buscar el texto completo */
  private resumingAfterCut = false;
  private messageId?: string;
  private finished = false;
  private pollTimer?: ReturnType<typeof setTimeout>;
  /** señal del AbortController compartido (dataChat.abortSignal) de la pregunta en curso */
  private abortSignal?: AbortSignal;
  private removeAbortListener?: () => void;

  /** polling de /response: ~1s entre intentos, ~60s de techo (la generación tarda ~10-15s). */
  private static readonly POLL_INTERVAL_MS = 1000;
  private static readonly POLL_MAX_ATTEMPTS = 60;

  // Cola SECUENCIAL de acciones estructuradas (create_*, check_valid_form).
  // Igual que WebSocketService.setupToolsPipeline: concatMap procesa una a una.
  // Imprescindible: create_starting_point geocodifica async y check_valid_form
  // valida startLocationAddress; en paralelo, check_valid_form gana la carrera y
  // pide "el punto de inicio" aunque el backend lo haya mandado bien.
  private actionQueue$ = new Subject<any>();
  private actionSub?: Subscription;

  constructor() {
    this.actionSub = this.actionQueue$
      .pipe(
        concatMap((action) =>
          this.toObservable(this.actionService.actionFromChatBot(action)).pipe(
            timeout({ each: 30000 }),
            catchError((e) => {
              this.debug.error(e, '[AskStreaming] action falló');
              return EMPTY; // sigue con la siguiente acción
            }),
            concatMap(() => of(null).pipe(delay(300))), // respiro antes de la siguiente
          ),
        ),
      )
      .subscribe();

    // HRS-3045: render_done — el servicio de animación notifica cada mensaje cuyo
    // render quedó completado; la medición marca tras el paint (doble rAF).
    this.textAnimation.messageRendered$.subscribe((id) => this.perf.onMessageRendered(id));
  }

  private toObservable(value: any) {
    if (value?.subscribe) return value;      // ya es Observable
    if (value instanceof Promise) return from(value);
    return of(value);
  }

  private getRouteToken(){
    let currentRoute: any = this.activatedRoute.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute.snapshot.paramMap.get('route-token');
  }

  async sendAsk(item: { content: string }): Promise<void> {
    this.reset();
    this.perf.beginTurn(); // ask_start (el message_id llega después)
    this.hookSharedAbortController();

    this.dataChat.removeErrorMsgs();
    this.dataChat.hasHistory.set(true);
    this.dataChat.isStreaming.set(true);
    this.dataChat.chatIsThinking.set(true);

    const body = {
      user_id: this.dataChat.token() ?? 'anonymous',
      service: Number(this.dataChat.id()),
      content: item.content,
      question: item.content,
      // Token de ruta: servicio primero (fiable en site y embed una vez existe
      // ruta); getRouteToken() (param URL) solo como fallback para el micro-hueco
      // de carga inicial del site. No se replica el fallback muerto de
      // sessionStorage('customRouteToken') del WS (nunca se escribe).
      route_token: this.customRouteService.token() ?? this.getRouteToken(),
      // "hay una ruta cargada/activa en el planner" (no "tiene ediciones sin
      // guardar"). Igual que _hasActiveRoute() del WS (webSocket.service.ts).
      route_active: this.editRouteService.editedCustomRouteData() !== null,
      // HRS-2579: destino desde el que se abrió el chat (ver webSocket.service).
      // Un chatbot de comarca atiende a varias apps y el `service` no distingue.
      app: (this.rest.subDomain || '').trim() || undefined
    };

    try {
      const res = await fetch(`${environment.apiAssistantUrlBase}/api/v3/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: this.abortSignal,
      });

      if (!res.ok)
        throw new Error(`POST /api/ask -> ${res.status}`);

      const data = await res.json();
      const messageId: string | undefined = data?.message_id;

      if (!messageId)
        throw new Error('respuesta de /api/ask sin message_id');

      this.messageId = messageId;
      this.perf.attachId(messageId);        // queued (+ ask_start retroactivo)
      this.openStream(messageId);           // tokens en vivo (cuando el backend los emita)
      this.pollFinalResponse(messageId, 0); // red de seguridad: respuesta definitiva
    } catch (err) {
      // Abort = stop del usuario (↻ del header / ⏹ del input): cancel() ya apagó
      // el loader; no pintar la burbuja de error de conexión.
      if ((err as any)?.name === 'AbortError') return;
      this.debug.error(err, '[AskStreaming] POST /api/ask falló');
      this.fail();
    }
  }

  /**
   * Cancelación por el usuario (HRS-2810): reutiliza el AbortController COMPARTIDO
   * del chat (dataChat.abortSignal). resetChat() (chatHeader), handleStopPlaying()
   * y handleStreamingStop() (chatInputForm) ya hacen dataChat.abortSignal().abort(),
   * así que escuchando su evento 'abort' esos botones también cortan este camino
   * SSE (POST /ask, EventSource y polling) sin tocar los componentes.
   */
  private hookSharedAbortController(): void {
    let controller = this.dataChat.abortSignal();
    // Un AbortController abortado queda "quemado" y solo startRecording lo renueva
    // (p.ej. tras un stop o un resetChat). Renovarlo aquí para que la nueva
    // pregunta no nazca ya cancelada.
    if (controller.signal.aborted) {
      controller = new AbortController();
      this.dataChat.abortSignal.set(controller);
    }

    const onAbort = () => this.ngZone.run(() => this.cancel());
    controller.signal.addEventListener('abort', onAbort, { once: true });
    this.abortSignal = controller.signal;
    this.removeAbortListener = () => controller.signal.removeEventListener('abort', onAbort);
  }

  /**
   * Stop del usuario: corta stream/polling en curso y apaga el loader SIN mensaje
   * de error. La animación de tipeo se cancela revelando lo ya recibido (mismo
   * criterio que WebSocketService.cancelCompletion -> textAnimation.cancel(id)).
   */
  cancel(): void {
    if (this.finished) return;
    this.finished = true;
    this.detachAbortListener();
    this.cancelPoll();
    this.closeStream();
    if (this.messageId) {
      this.perf.close(this.messageId, 'cancel', true);
      try { this.textAnimation.cancel(this.messageId); } catch {}
    }
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsThinking.set(false);
  }

  /** tipos de evento del contrato HRS-2418 */
  private static readonly SSE_EVENTS = ['token', 'final', 'action', 'action_tag', 'done'];

  private openStream(messageId: string): void {
    const url = `${environment.apiAssistantUrlBase}/api/v3/stream/${messageId}`;
    const es = new EventSource(url);
    this.eventSource = es;

    // HRS-3045: stream_open real (antes no existía señal de apertura del EventSource)
    es.onopen = () => this.perf.mark(messageId, 'stream_open');

    // El backend puede emitir eventos SIN nombre (data: {type,...}) o CON nombre
    // (event: token\ndata: {...}). EventSource.onmessage solo capta los primeros,
    // así que registramos también listeners por nombre. El 'type' efectivo es el
    // del payload o, en su defecto, el nombre del evento SSE.
    es.onmessage = (ev: MessageEvent) => this.onFrame(messageId, ev.data, undefined);
    for (const name of AskStreamingService.SSE_EVENTS)
      es.addEventListener(name, (ev: MessageEvent) => this.onFrame(messageId, ev.data, name));

    // onerror dispara cuando el server cierra la conexión o cuando se corta
    // (red, proxy, idle). Antes se finalizaba siempre aquí, y como el primer
    // token ya había cancelado el polling, un corte a mitad dejaba la respuesta
    // cortada (o en blanco) sin aviso. Ahora solo se da por buena si ya llegó
    // 'final'; si no, el polling de /response trae el texto completo o, si no
    // llega, fail() avisa al usuario.
    es.onerror = (e) => {
      this.debug.warn(e, '[AskStreaming] SSE onerror (cierre/corte)');
      this.ngZone.run(() => this.onStreamError(messageId));
    };
  }

  private onStreamError(messageId: string): void {
    if (this.finished || this.messageId !== messageId) return;
    // Cerrar ya: EventSource reconecta solo y repetiría el turno a medias.
    this.closeStream();
    if (this.finalReceived) {
      this.finish('sse-closed-after-final');
      return;
    }
    // Sin tokens el polling sigue vivo y él cierra el turno. Con tokens se
    // canceló al primero: se reanuda para traer el texto completo.
    if (this.tokenReceived && !this.resumingAfterCut) {
      this.resumingAfterCut = true;
      this.cancelPoll();
      this.pollFinalResponse(messageId, 0);
    }
  }

  private onFrame(messageId: string, raw: string, eventName?: string): void {
    let evt: any;
    try { evt = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return; }
    // tipo efectivo: el del payload o el nombre del evento SSE
    const type = evt?.type ?? eventName;
    this.ngZone.run(() => this.handleEvent(messageId, { ...evt, type }));
  }

  private handleEvent(messageId: string, evt: any): void {
    // Frame terminal del stream ({status:"completed"} sin tokens). HOY llega
    // prematuramente, así que NO finalizamos aquí: dejamos que el polling de
    // /response pinte el texto cuando esté listo. Solo finalizamos por esta vía
    // si el stream YA emitió tokens reales (futuro).
    if (!evt?.type && (evt?.status === 'completed' || evt?.status === 'done')) {
      if (this.tokenReceived)
        this.finish();
      return;
    }

    switch (evt?.type) {
      // Fragmento en vivo: enqueueText crea/anima la burbuja y extrae los [ACTION:..] tags.
      case 'token':
        if (evt.content) {
          if (!this.tokenReceived) {
            this.perf.mark(messageId, 'first_token_received');
            this.perf.setVia(messageId, 'sse-tokens');
          }
          this.tokenReceived = true;
          this.cancelPoll(); // el stream toma el control; no pintar también por /response
          this.textAnimation.enqueueText(messageId, evt.content, undefined, { batch: true });
        }
        break;

      // [ACTION:SHOW_POI:{...}] -> mismo parser que el texto (processAssistantTools).
      case 'action_tag':
        if (evt.content) {
          if (!this.tokenReceived) {
            this.perf.mark(messageId, 'first_token_received');
            this.perf.setVia(messageId, 'sse-tokens');
          }
          this.tokenReceived = true;
          this.cancelPoll();
          this.textAnimation.enqueueText(messageId, evt.content, undefined, { batch: true });
        }
        break;

      // Acción estructurada. El SSE la anida en evt.action {...}; el handler hace
      // switch(result.action) (string). TODO(refinar con un payload real): confirmar
      // el nivel de anidación (evt.action vs evt).
      case 'action':
        // Encolar para procesamiento SECUENCIAL (ver actionQueue$): create_starting_point
        // (geocoding async) debe completar ANTES de check_valid_form, o este ve el origen
        // vacío y pide el punto de inicio. Igual que el pipeline del WebSocket.
        this.actionQueue$.next(evt.action ?? evt);
        break;

      // Texto limpio definitivo (post-procesado en backend): SIEMPRE manda.
      // Reconciliación: sustituye lo pintado por el texto final, haya habido
      // streaming de tokens o no. Así desaparecen restos crudos (PROACTIVE,
      // markers) y se respeta el idioma/limpieza definitivos. Si no hubo
      // tokens, simplemente pinta el final.
      case 'final':
        this.finalReceived = true;
        this.cancelPoll();
        this.perf.mark(messageId, 'final_received');
        if (!this.tokenReceived)
          this.perf.setVia(messageId, 'final-only');
        if (evt.text)
          this.textAnimation.setFinalContent(messageId, evt.text);
        // NO cerrar el stream aquí. Las actions (SHOW_POI/POIS/WEB) las inyecta
        // el action_enforcer DESPUÉS del texto, así que llegan TRAS 'final'.
        // Cerrar en 'final' mataba el EventSource y las tarjetas nunca se
        // procesaban. El cierre se hace solo en 'done' (el último evento).
        break;

      case 'done':
        this.finish();
        break;

      default:
        this.debug.warn(evt, '[AskStreaming] evento SSE desconocido');
    }
  }

  /**
   * Red de seguridad: GET /api/chat/response hasta status==="completed".
   * Es lo que pinta la respuesta mientras el stream no emita tokens. Se cancela
   * en cuanto llega un token por el stream (this.tokenReceived), salvo que el
   * stream se corte antes de 'final' (resumingAfterCut, ver onStreamError).
   */
  private async pollFinalResponse(messageId: string, attempt: number): Promise<void> {
    if (this.pollSuperseded() || this.messageId !== messageId)
      return;

    try {
      const res = await fetch(
        `${environment.apiAssistantUrlBase}/api/chat/response/${messageId}`,
        { signal: this.abortSignal },
      );
      if (res.ok) {
        const data = await res.json();
        if (this.pollSuperseded()) return;
        const content: string | undefined = data?.content ?? data?.text;
        if (data?.status === 'completed') {
          const resumed = this.resumingAfterCut;
          if (!resumed) this.perf.setVia(messageId, 'poll'); // si se reanuda, la vía sigue siendo sse-tokens
          if (content)
            this.ngZone.run(() => resumed
              // ya hay texto parcial pintado: se sustituye entero, no se añade
              ? this.textAnimation.setFinalContent(messageId, content)
              : this.textAnimation.enqueueText(messageId, content, undefined, { batch: true }));
          else
            this.debug.warn(data, '[AskStreaming] /response completed sin content');
          this.finish(resumed ? 'poll-after-sse-cut' : 'poll');
          return;
        }
        // status === "processing" -> seguimos polleando
      }
    } catch (err) {
      if ((err as any)?.name === 'AbortError') return; // stop del usuario: cancel() ya cerró todo
      this.debug.warn(err, '[AskStreaming] poll /response falló (reintenta)');
    }

    if (this.finished) return; // cancelado/finalizado mientras el fetch estaba en vuelo

    if (attempt + 1 >= AskStreamingService.POLL_MAX_ATTEMPTS) {
      this.debug.error('timeout', '[AskStreaming] /response no completó a tiempo');
      this.fail();
      return;
    }
    this.pollTimer = setTimeout(
      () => this.pollFinalResponse(messageId, attempt + 1),
      AskStreamingService.POLL_INTERVAL_MS,
    );
  }

  private finish(cause: string = 'done'): void {
    if (this.finished) return;
    this.finished = true;
    this.detachAbortListener();
    this.cancelPoll();
    this.closeStream();
    if (this.messageId) {
      // done_received con su causa (done real / sse-error / poll)
      this.perf.close(this.messageId, cause);
      // HRS-3045: señal explícita de fin para el modo batch — vuelca lo pendiente
      // y ejecuta los efectos de completado (finished, proactivos, render_done).
      this.textAnimation.markStreamEnded(this.messageId);
    }
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsThinking.set(false);
  }

  private fail(): void {
    if (this.finished) return;
    this.finished = true;
    this.detachAbortListener();
    this.cancelPoll();
    this.closeStream();
    if (this.messageId) {
      this.perf.close(this.messageId, 'fail', true);
      this.textAnimation.markStreamEnded(this.messageId);
    }
    this.dataChat.showConnectionErrorMessage();
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsThinking.set(false);
  }

  private reset(): void {
    this.detachAbortListener();
    this.cancelPoll();
    this.closeStream();
    this.tokenReceived = false;
    this.finalReceived = false;
    this.resumingAfterCut = false;
    this.finished = false;
    this.messageId = undefined;
    this.abortSignal = undefined;
  }

  private detachAbortListener(): void {
    this.removeAbortListener?.();
    this.removeAbortListener = undefined;
  }

  private cancelPoll(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  /** el turno terminó o el stream manda (hay tokens y no se ha cortado) */
  private pollSuperseded(): boolean {
    return this.finished || (this.tokenReceived && !this.resumingAfterCut);
  }

  closeStream(): void {
    if (this.eventSource) {
      try { this.eventSource.close(); } catch {}
      this.eventSource = undefined;
    }
  }
}
